import { deepPrimaryType, paywallFooterElement, revcatOfferingId, stripeConfigItem } from '@inkitt/types-common';
import { z } from 'zod';
import { AdProviderTypes } from './adProvider';
import { SubscriptionPeriodLiterals } from './paywall';

/** This type of data might be stored in Redux Slice. So while allowing flexibility, we also ensure that we do not allow functions and only primary data types */
export const remoteButtonOverrides = deepPrimaryType;
export type RemoteButtonOverrides = z.infer<typeof remoteButtonOverrides>;

export const genericPaywallConfig = z.object({
  titleString: z.string().optional(),
  subtitleString: z.string().optional(),
  descriptionString: z.string().optional(),
  backgroundImage: z.string().optional(),
  buttonString: z.string().optional(),
  discountTitleString: z.string().optional(),
  footerString: z.string().optional(),
});
export type GenericPaywallConfig = z.infer<typeof genericPaywallConfig>;

export const exitIntentPayload = z.object({
  revcatOfferingId,
  offerIdentifier: z.string().optional(),
  minimumPaywallSeenCount: z.number().optional(),
  offerDurationMillis: z.number().optional(),
  paywallConfig: genericPaywallConfig.optional(),
});
export type ExitIntentPayload = z.infer<typeof exitIntentPayload>;

export const introOfferPayload = z.object({
  revcatOfferingId,
  offerIdentifier: z.string().optional(),
  minimumPaywallSeenCount: z.number().optional(),
  offerDurationMillis: z.number().optional(),
  calendarDaysBetweenPopup: z.number().optional(),
  popupConfig: genericPaywallConfig.omit({ backgroundImage: true }).optional(),
});
export type IntroOfferPayload = z.infer<typeof introOfferPayload>;

export const adsSubscriptionConfig = z.object({
  // defaults to applovin
  adsProvider: z.enum(AdProviderTypes).optional(),
  postTimerAdConfig: z
    .object({
      skipAdCost: z.number().optional(),
      showAdsStartingOnDay: z.number(),
    })
    .optional(),
});
export type AdsSubscriptionPayload = z.infer<typeof adsSubscriptionConfig>;

export const productConfig = z
  .object({
    productIdMatch: z.string(),
    title: z.string().optional(),
    subtitle: z.string().optional(),
  })
  .array();
export type ProductConfig = z.infer<typeof productConfig>;

export const subtitleStyle = z.enum(['default', 'no-box']);
export type SubtitleStyle = z.infer<typeof subtitleStyle>;

/**
 * Shape of the post-purchase CandyJar/GalateaTV cross-sell offer. Used both by the pricing test
 * (`postPurchaseGalateaTvOffer` below) and by the standalone `testCandyJarUpsell` AB test, which
 * carries its own copy of this payload so it can be run fully independently of the pricing tests.
 */
export const postPurchaseGalateaTvOfferPayload = z.object({
  /** Stripe SKU of the CandyJar offer (e.g. `cju-ws-wap`); appended to the checkout URL as `sku`. */
  sku: z.string(),
  /** Promo applied at checkout (and to the displayed price); appended to the checkout URL as `promocode`. */
  promocode: z.string(),
  /**
   * galatea-web checkout base URL opened by the CTA (auth token is injected automatically). The screen
   * appends `sku=<sku>&promocode=<promocode>` before opening it.
   */
  ctaUrl: z.string().optional(),
  /** Optional display strings; static marketing copy comes from translations. */
  priceLabel: z.string().optional(),
  priceSuffix: z.string().optional(),
  strikethroughPrice: z.string().optional(),
  discountLabel: z.string().optional(),
});
export type PostPurchaseGalateaTvOfferPayload = z.infer<typeof postPurchaseGalateaTvOfferPayload>;

export const pricingTestPayload = z.object({
  revcatOfferingId,
  productConfig: productConfig.optional(),
  subtitleStyle: subtitleStyle.optional(),
  shouldBlockAppOnUnsubscribe: z.boolean().optional(),
  shouldShowUserCallOffer: z.boolean().optional(),
  shouldSkipPromoOffer: z.boolean().optional(),
  shouldSplitTextAndAudiobookSubscription: z.boolean().optional(),
  /**
   * Shows the Listen Now button (reader headphones + summary bar) to anyone holding ANY active
   * Galatea subscription — text-only included — regardless of `testListenNowForTextBook`. Only the
   * pricing arms that set it are affected; elsewhere that AB test keeps full control of the button.
   */
  showListenNowButtonAfterUserSubscribed: z.boolean().optional(),
  showAudiobookUpsellAfterReadingPurchase: z.boolean().optional(),
  splitTestProductEntitlements: z
    .object({
      unlimitedProduct: z.array(z.string()),
      textOnlyProduct: z.array(z.string()),
      audioOnlyProduct: z.array(z.string()).optional(),
      audioAddonProduct: z.array(z.string()).optional(),
      /**
       * Product whose store price is struck through on the post-purchase audiobook upsell as the
       * "regular" price, in both iOS (`com.id.p1y`) and Android (`com.id:p1y`) forms. Its period may
       * differ from the add-on's — the upsell converts it to the add-on's billing period before
       * comparing. The product must be part of the resolved RevenueCat offering, otherwise the app
       * has no price for it and the upsell shows no strikethrough at all.
       */
      audioAddonAnchorProduct: z.array(z.string()).optional(),
      readerAddonProduct: z.array(z.string()).optional(),
    })
    .optional(),
  shouldShowUpgradeScreens: z.boolean().optional(),
  swapSubscriptionProducts: z.boolean().optional(),
  subscriptionPointsPerMonth: z.number().optional(),
  bookPerMonth: z.number().optional(),
  exitIntentConfig: exitIntentPayload.optional(),
  introOfferConfig: introOfferPayload.optional(),
  overrideBaseSubscriptionPeriod: SubscriptionPeriodLiterals.optional(),
  recommendedPointsPackageIndex: z.number().optional(),
  familyPlan: z
    .object({
      discoveryUpgradeBannerDelayDays: z.number().optional(),
    })
    .optional(),
  pointsFooterElement: paywallFooterElement.optional(),
  organicFreeTrialSettings: z
    .object({
      freeTrialRevcatOfferingId: revcatOfferingId,
      minimumPaywallSeenCount: z.number(),
      propensityThreshold: z.number().optional(),
    })
    .optional(),
  isWebRedesignEligible: z.boolean().optional(),
  adsSubscriptionConfig: adsSubscriptionConfig.optional(),
  overrideFreeBookShelf: z
    .record(
      z.string(),
      z.array(
        z.object({
          id: z.string(),
          start: z.number(),
          end: z.number(),
        }),
      ),
    )
    .optional(),

  // optional config to override the base stripe settings coming from featureWebPayment
  stripeSettings: z.object({ config: z.record(z.string(), stripeConfigItem).optional() }).optional(),
  showMonthlyOptimizedRecommendations: z.boolean().optional(),

  // optional config to show a specific discount when upgrading subscriptions
  upgradeOfferConfig: z
    .object({
      iosPromotionalOfferId: z.string().optional(),
      androidSubscriptionOptionId: z.string().optional(),
      stripe: z
        .object({
          promocode: z.string(),
          discountPercentage: z.number(),
        })
        .optional(),
    })
    .optional(),

  // optional config to disable the countdown flow
  disableWaitTimers: z.boolean().optional(),

  /**
   * whether an active Points+ subscription is itself a reason to never sit out a
   * countdown — the reader and the audio player unlock the next chapter out of the wallet the
   * subscription tops up, and the reader's next-chapter CTA names the price it is about to charge.
   *
   * Its own field rather than a widening of `disableWaitTimers`: the arms already selling Points+
   * were measured with the countdown in place, and that arm's payload is immutable, so the
   * exemption can only arrive as something a new arm opts into. Read through
   * `areWaitTimersDisabled` / `useAreWaitTimersDisabled`, never directly.
   */
  disableWaitTimersForPointsSubscribers: z.boolean().optional(),

  stripeBottomSheetVariant: z.enum(['v1', 'v2']).optional(),

  /**
   * When true, the client signals Saturn to issue a golden-hour offer; the offer body itself
   * is stored on the `featureGoldenHour` Unleash feature toggle, not in this payload. Only
   * respected on the `golden_hour` variant of `testPricingV4`.
   */
  isGoldenHourAvailable: z.boolean().optional(),

  /**
   * When true, the points and subscription golden-hour discounts render with the reveal
   * animation (strike-through, price drop + impact burst, badge wipe, card glow). Only
   * respected on the `golden_hour_v2` iteration of `testPricingV4`; the existing
   * `golden_hour` iteration leaves this unset so its behaviour is unchanged.
   */
  isGoldenHourAnimationEnabled: z.boolean().optional(),

  /**
   * Celebration Screen shown to users who just completed an annual subscription purchase,
   * offering a one-time discounted renewal for their next year. Only respected on the
   * `post_purchase_renewal_offer` variant of `testPricingV4`. The redemption path depends
   * on the source: Stripe → galatea-web renewal flow with `promocode`; iOS in-app →
   * RevenueCat promotional offer via `promoOffer`; Android in-app → one-time RC offering
   * via `revcatExtraOffer` followed by a server-side Google subscription deferral.
   */
  postPurchaseRenewalOffer: z
    .object({
      promocode: z.string(),
      /** Fractional discount applied to the purchased product's price (e.g. 0.25 for 25% off). */
      discountRate: z.number().min(0).max(1),
      promoOffer: z.string().optional(),
      revcatExtraOffer: z.string().optional(),
      /** Days to extend the active Google Play subscription by after a successful Android `revcatExtraOffer` purchase. RevenueCat allows 1-365. */
      extendByDays: z.number().int().min(1).max(365).optional(),
    })
    .optional(),

  /**
   * Post-purchase cross-sell shown after a Galatea Stripe subscription purchase, offering a
   * GalateaTV subscription. The CTA opens the GalateaTV checkout page on galatea-web (via
   * `appToWebService.toWebWithAuthTokenV2`, which injects the user's auth token), mirroring the
   * renewal-offer CTA. Stripe flow only. The presence of this object enables the offer.
   */
  postPurchaseGalateaTvOffer: postPurchaseGalateaTvOfferPayload.optional(),

  /**
   * the offer to upgrade to unlimited that follows a purchase leaving the buyer on Points+
   * (`PostPurchasePointsUpgradeOffer`, an exclusive step of the DEV-5712 upsell queue). Its PRESENCE
   * is what runs the offer, the way `postPurchaseGalateaTvOffer` enables its own — only the arms
   * testing it configure the object; every other Points+ arm, including the ones already live, keeps
   * the purchase ending where it did.
   *
   * Stated rather than inferred from the rest of the payload: the default target is the offering's
   * `metadata.baseAnnualProductIds`, which the older Points+ arms may well also carry (their paywall's
   * own upgrade tab sells the same thing), so nothing else in the payload can stand in for "this arm
   * runs the post-purchase offer".
   */
  pointsUpgradeOffer: z
    .object({
      /**
       * Offering the upgrade product is taken from, instead of `metadata.baseAnnualProductIds`. It is
       * deliberately NOT the arm's own `revcatOfferingId`: the subscription paywall renders exactly one
       * offering, so a product that must be purchasable from this offer without also showing up there as
       * a plan row has to live in an offering of its own. Expected to hold a single package.
       *
       * Left unset, the arm runs the offer against today's target — which is what makes an arm selling a
       * different product measurable against one that does not.
       */
      revcatOfferingId: revcatOfferingId.optional(),
    })
    .optional(),
});
export type PricingTestPayload = z.infer<typeof pricingTestPayload>;

export const pricingTestVariants = z.enum(['control', 'aa_variant_1', 'white_payload', 'organic_50']);
export type PricingTestVariants = z.infer<typeof pricingTestVariants>;

export type StripeBottomSheetVariant = 'v1' | 'v2';

export const pricingTestDefinition = {
  definition: pricingTestPayload,
  variants: pricingTestVariants,
};

export const OFFER_BASELINE_ARM_PREFIX = 'default_';
export const OFFER_OVERRIDE_ARM_PREFIX = 'override_';

/** Baseline arm names are decided in Unleash, hence `z.string()`. */
export const offerBaselineDefinition = {
  definition: pricingTestPayload,
  variants: z.string(),
};

export const offerExperimentDefinition = <const Treatments extends readonly [string, ...string[]]>(
  ...treatments: Treatments
) => ({
  definition: pricingTestPayload,
  variants: z.enum(['control', ...treatments]),
});

/**
 * Harlequin subscription config, carried by the `featureHarlequinPricing` flag. Since DEV-6006 this flag
 * alone decides whether Harlequin is visible and what it costs — the retired `testHarlequinPricing`
 * experiment no longer gates any surface, and the post-purchase offer moved to `testHarlequinUpsell`.
 */
export const harlequinConfigPayload = z.object({
  revcatOfferingId,
  sku: z.string().optional(),
  stripeSettings: z.object({ config: z.record(z.string(), stripeConfigItem).optional() }).optional(),
  showIAPLink: z.boolean().optional(),
  /**
   * when true, the "separate subscription" clarification UX is shown to reduce the
   * confusion that Harlequin is included in Galatea Unlimited — the book summary drops the
   * co-branded "Galatea x Harlequin" lockup for an "Exclusive to Harlequin Subscribers 🔒" banner
   * (and, where built, the reader continue CTA/subtext and updated paywall copy). Left unset, the
   * original co-branded experience is preserved unchanged.
   */
  upgradeText: z.boolean().optional(),
});
export type HarlequinConfigPayload = z.infer<typeof harlequinConfigPayload>;

/**
 * payload of `featureHarlequinPricingV2`, the flag that turns the Galatea × Harlequin bundle
 * on. It is a second flag rather than extra fields on `featureHarlequinPricing` because shipped variant
 * payloads are immutable (see `libs/utils-unleash/CONVENTIONS.md`), and it doubles as the feature's
 * single switch: a reader who resolves this payload gets the bundle plan, its paywall row, and Harlequin
 * visibility without a Galatea subscription. Readers who do not fall back to the V1 config unchanged.
 */
export const harlequinConfigV2Payload = harlequinConfigPayload.extend({
  /**
   * The bundle — one subscription granting a Galatea entitlement plus `harlequin:unlimited` — which takes
   * the family plan's slot on the subscription paywall. Both forms of the store id may be listed, iOS
   * (`com.id.p1y`) and Android (`com.id:p1y`).
   *
   * Which one is offered follows the reader's pricing arm, because the bundle has to pair with the
   * Galatea plan beside it: `textBundleProduct` grants text-only access and belongs in the split
   * text/audio arms, `unlimitedBundleProduct` grants full access and belongs everywhere else. A variant
   * that configures neither keeps the family plan.
   */
  textBundleProduct: z.array(z.string()).optional(),
  unlimitedBundleProduct: z.array(z.string()).optional(),
});
export type HarlequinConfigV2Payload = z.infer<typeof harlequinConfigV2Payload>;

/**
 * Payload of the `testHarlequinUpsell` experiment: the offer applied to the Harlequin product when it
 * is sold as a post-purchase upsell (e.g. a discounted annual price). Separate from the always-on
 * `featureHarlequinPricing` config because whether the upsell runs at all is an experiment, while
 * Harlequin's own availability and pricing are not.
 *
 * The redemption path depends on the purchase channel: Stripe web checkout uses `promocode`; Play Store
 * uses the base-plan `playStoreOfferId` on the Android product.
 */
export const harlequinUpsellPayload = z.object({
  /**
   * Whether this arm runs the upsell. Stated rather than inferred from the rest of the payload: every
   * field below is optional, so a control-like arm configured with `{}` parses cleanly and would
   * otherwise read as an offer. Mirrors `testAdSkips.isEnabled` — only the treatment arm sets it.
   */
  isEligible: z.boolean().optional(),
  /**
   * RevenueCat offering the offer's package is resolved from (may differ from the base
   * `revcatOfferingId` in `featureHarlequinPricing`). Used by the store channels (iOS/Play) to fetch
   * the discounted package; not needed for Stripe.
   */
  revcatOfferingId: revcatOfferingId.optional(),
  /**
   * Product SKU the offer applies to (may differ from the base `sku`, e.g. a dedicated annual SKU). On
   * iOS it is the productId passed to `getAvailablePromotionalOffer`; on Play it is the base product of
   * the `playStoreOfferId`.
   */
  sku: z.string().optional(),
  /** Stripe: promo code applied at web checkout (and to the displayed price). */
  promocode: z.string().optional(),
  /** Play Store: base-plan offer id applied to the Android product at purchase. */
  playStoreOfferId: z.string().optional(),
});
export type HarlequinUpsellPayload = z.infer<typeof harlequinUpsellPayload>;

export const harlequinUpsellTestVariants = z.enum(['control', 'harlequin_upsell']);
export type HarlequinUpsellTestVariants = z.infer<typeof harlequinUpsellTestVariants>;

export const harlequinUpsellTestDefinition = {
  definition: harlequinUpsellPayload.optional(),
  variants: harlequinUpsellTestVariants,
};

/**
 * Payload for the `testHarlequinPricing` price experiment. Extends the shared config with `enabled`,
 * which distinguishes treatment arms (offering override applies) from `control` (`enabled: false` →
 * fall back to the always-on `featureHarlequinPricing` default).
 */
export const harlequinPricingTestPayload = harlequinConfigPayload.extend({
  enabled: z.boolean(),
});
export type HarlequinPricingTestPayload = z.infer<typeof harlequinPricingTestPayload>;

export const harlequinPricingTestVariants = z.enum([
  'control',
  'higher',
  'lower',
  'higher_with_iap',
  'lower_with_iap',
  // annual Harlequin upsell ($24.99, renews at $69.99) shown as part of the concatenated
  // post-purchase upsells. Pricing/renewal terms are delivered via this variant's Unleash payload.
  'annual_upsell',
]);
export type HarlequinPricingTestVariants = z.infer<typeof harlequinPricingTestVariants>;

export const harlequinPricingTestDefinition = {
  definition: harlequinPricingTestPayload,
  variants: harlequinPricingTestVariants,
};
